import {
  CABIN_BAG_CAMEL_CASE,
  CHECKED_BAG_CAMEL_CASE,
} from '@web-platform/shared-universal-constants/src/queryParamMapper';
import { CLIENTS_SEARCH } from '@web-platform/shared-universal-types-tracking/src/funnelEvents/funnelEventsTypes';
import { normalizeSearchLegs } from '@web-platform/shared-universal-utils-frontend/src/helpers/normalizeSearchLegs';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { GLOBAL } from 'saddlebag-browser';

import { getSearch } from './funnelEventsDimensions/searchDimensions';

import type { SearchDimensions } from './funnelEventsDimensions/searchDimensions';
import type { SearchState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type {
  FunnelEventProperty,
  FunnelEventEventName,
} from '@web-platform/shared-universal-types-tracking/src/funnelEvents/funnelEventsTypes';

type SearchEventProps = {
  eventName: string;
  previousEventId: string | null;
  search: SearchState;
  viewId: string;
  businessDomain: NonNullable<SearchDimensions['businessDomain']>;
};

type SearchEventPayload = {
  eventName: FunnelEventEventName;
  funnelDimensions: FunnelEventProperty;
  opts: { messageGuid: string };
};

/**
 * Builds the `clients.Search` Trusted Funnel event payload.
 *
 * @returns {SearchEventPayload} `clients.Search` event payload
 */
export const searchEvent = ({
  businessDomain,
  eventName: analyticsEventName,
  previousEventId,
  search,
  viewId,
}: SearchEventProps): SearchEventPayload => {
  const childAges = search.child_ages ?? [];
  const { locale } = getCulture();

  // TODO: LUNA-3866: Remove call to normalizeSearchLegs below once userSearch has been converged
  const normalizedLegs = normalizeSearchLegs(search.legs ?? []);
  const firstLeg = normalizedLegs[0];
  const lastLeg = normalizedLegs.length > 1 ? normalizedLegs.at(-1) : undefined;
  const legs = normalizedLegs.map((leg) => ({
    date: leg.date,
    destination: leg.destination?.id,
    origin: leg.origin?.id,
  }));
  const fareAttributeSet = new Set((search.fareAttributes ?? []).map(String));

  const searchDimensions = getSearch({
    businessDomain,
    eventName: analyticsEventName,
    previousEventId,
    commonSearchData: {
      adults: search.adults,
      alternativeDestinations: search.alternativeDestinations,
      alternativeOrigins: search.alternativeOrigins,
      cabinClass: search.cabinClass,
      children: childAges.filter((age) => age >= 2).length,
      destinationCodeLeg1: firstLeg?.destination?.id,
      href: GLOBAL.getWindow()?.location?.href,
      inboundDate: lastLeg?.date ?? '',
      infants: childAges.filter((age) => age < 2).length,
      isCabinBagRequired: fareAttributeSet.has(CABIN_BAG_CAMEL_CASE),
      isCheckedBagRequired: fareAttributeSet.has(CHECKED_BAG_CAMEL_CASE),
      legs,
      locale,
      nonAdultAges: childAges,
      originCodeLeg1: firstLeg?.origin?.id,
      outboundDate: firstLeg?.date ?? '',
      preferDirects: search.prefer_directs,
      requestId: viewId,
      searchEventGuid: viewId,
      tripType: search.tripType,
      viewGuid: viewId,
    },
  });

  return {
    eventName: CLIENTS_SEARCH,
    funnelDimensions: searchDimensions,
    opts: { messageGuid: viewId },
  };
};
